<template>
  <div class="variables-container">
    <!-- Composant de notification -->
    <div class="notification-wrapper" v-if="notification.show">
      <div class="notification" :class="notification.type">
        <svg v-if="notification.type === 'success' || notification.type === 'saved'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 6L9 17l-5-5"></path>
        </svg>
        <svg v-else-if="notification.type === 'error'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
        <svg v-else-if="notification.type === 'saving'" class="spinning" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
        </svg>
        <span>{{ notification.message }}</span>
      </div>
    </div>

    <!-- Loading spinner -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
        </svg>
      </div>
      <p>Chargement...</p>
    </div>

    <!-- Main content -->
    <div v-else class="variables-content">
      <!-- Header -->
      <div class="variables-header">
        <button class="back-button" @click="goBack">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Retour
        </button>
        
        <div class="header-content">
          <h1>Variables du Chatbot</h1>
          <p v-if="chatbot">{{ chatbot.bot_name }}</p>
        </div>

        <div class="header-actions">
           
          
          <button class="add-variable-btn" @click="addVariable">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Ajouter Variable
          </button>
        </div>
      </div>

      <!-- Variables List -->
      <div class="variables-list">
        <div v-if="variables.length === 0" class="empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
            <path d="M3 7V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2"></path>
            <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7"></path>
            <line x1="12" y1="11" x2="12" y2="17"></line>
            <line x1="9" y1="14" x2="15" y2="14"></line>
          </svg>
          <h3>Aucune variable créée</h3>
          <p>Commencez par créer votre première variable pour ce chatbot.</p>
          <button class="primary-btn" @click="addVariable">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Créer une variable
          </button>
        </div>

        <div v-else>
          <!-- Variables Grid -->
          <div class="variables-grid">
            <div 
              v-for="(variable, index) in variables" 
              :key="index" 
              class="variable-card"
              :class="{ 'has-errors': getVariableErrors(variable).length > 0 }"
            >
              <div class="card-header">
                <span class="variable-index">#{{ index + 1 }}</span>
                <button 
                  class="delete-variable-btn"
                  @click="removeVariable(index)"
                  title="Supprimer cette variable"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="3,6 5,6 21,6"></polyline>
                    <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              </div>

              <div class="variable-fields">
                <div class="field-group">
                  <label>Identifiant</label>
                  <input 
                    type="text" 
                    v-model="variable.id" 
                    placeholder="ex: user_name, age, email..."
                    class="variable-input"
                    :class="{ 'error': !variable.id }"
                    @input="validateVariable(variable)"
                  />
                  <span v-if="!variable.id" class="field-error">L'identifiant est requis</span>
                </div>

                <div class="field-group">
                  <label>Nom d'affichage (slug)</label>
                  <input 
                    type="text" 
                    v-model="variable.slug" 
                    placeholder="ex: Nom d'utilisateur, Âge, Email..."
                    class="variable-input"
                    :class="{ 'error': !variable.slug }"
                    @input="validateVariable(variable)"
                  />
                  <span v-if="!variable.slug" class="field-error">Le nom d'affichage est requis</span>
                </div>

                <div class="field-group">
                  <label>Type de variable</label>
                  <select 
                    v-model="variable.type" 
                    class="variable-select"
                    :class="{ 'error': !variable.type }"
                    @change="validateVariable(variable)"
                  >
                    <option value="">Sélectionnez un type</option>
                    <option value="text">Texte</option>
                    <option value="location">Localisation</option>
                    <option value="tel_sen">Téléphone (Sénégal)</option>
                    <option value="any_type">Type libre</option>
                  </select>
                  <span v-if="!variable.type" class="field-error">Le type est requis</span>
                </div>

                <div class="type-description">
                  <small>{{ getTypeDescription(variable.type) }}</small>
                </div>
              </div>

              <!-- Erreurs de validation -->
              <div v-if="getVariableErrors(variable).length > 0" class="validation-errors">
                <div v-for="error in getVariableErrors(variable)" :key="error" class="error-item">
                  {{ error }}
                </div>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="variables-actions">
            <button class="secondary-btn" @click="addVariable">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Ajouter une variable
            </button>
            
            <button 
              class="primary-btn" 
              @click="saveVariables"
              :disabled="!isFormValid || saving"
            >
              <svg v-if="saving" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spin">
                <circle cx="12" cy="12" r="10"></circle>
              </svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17,21 17,13 7,13 7,21"></polyline>
                <polyline points="7,3 7,8 15,8"></polyline>
              </svg>
              {{ saving ? 'Sauvegarde...' : 'Sauvegarder' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de confirmation de suppression -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="cancelDelete">
      <div class="modal-content delete-modal" @click.stop>
        <div class="modal-header">
          <div class="modal-icon delete-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3,6 5,6 21,6"></polyline>
              <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </div>
          <h3>Supprimer la variable</h3>
        </div>
        
        <div class="modal-body">
          <p>Êtes-vous sûr de vouloir supprimer cette variable ?</p>
          <div v-if="variableToDelete" class="variable-preview">
            <strong>{{ variableToDelete.id }}</strong> - {{ variableToDelete.slug }}
          </div>
          <p class="warning-text">Cette action est irréversible.</p>
        </div>

        <div class="modal-footer">
          <button @click="cancelDelete" class="cancel-btn">
            Annuler
          </button>
          <button @click="confirmDelete" class="delete-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3,6 5,6 21,6"></polyline>
              <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
            Supprimer
          </button>
        </div>
      </div>
    </div>

    <!-- API Test Modal -->
    <div v-if="showApiTest" class="modal-overlay" @click="closeApiTest">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Test des Endpoints API</h3>
          <button @click="closeApiTest" class="modal-close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="api-test-section">
            <h4>GET - Récupérer les variables</h4>
            <button @click="testGetVariables" class="test-btn" :disabled="testingGet">
              {{ testingGet ? 'Test en cours...' : 'Tester GET' }}
            </button>
            <pre v-if="getTestResult" class="api-result">{{ getTestResult }}</pre>
          </div>

          <div class="api-test-section">
            <h4>POST - Sauvegarder les variables</h4>
            <button @click="testPostVariables" class="test-btn" :disabled="testingPost">
              {{ testingPost ? 'Test en cours...' : 'Tester POST' }}
            </button>
            <pre v-if="postTestResult" class="api-result">{{ postTestResult }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
 import { axiosPeelo } from '@/utils/axios';

export default {
  name: 'VariablesView',
  data() {
    return {
      loading: true,
      saving: false,
      chatbot: null,
      variables: [],
      notification: {
        show: false,
        message: '',
        type: 'success',
        timeout: null
      },
      showApiTest: false,
      testingGet: false,
      testingPost: false,
      getTestResult: null,
      postTestResult: null,
      showDeleteModal: false,
      variableToDelete: null,
      indexToDelete: null,
      typeDescriptions: {
        text: 'Variable de type texte libre',
        location: 'Coordonnées géographiques (latitude, longitude)',
        tel_sen: 'Numéro de téléphone au format sénégalais',
        any_type: 'Accepte tout type de données'
      }
    }
  },
  computed: {
    chatbotId() {
      return this.$route.params.idchatbot
    },
    isFormValid() {
      if (this.variables.length === 0) return false
      return this.variables.every(variable => 
        variable.id && variable.slug && variable.type
      )
    }
  },
  mounted() {
    this.initializeData()
  },
  beforeUnmount() {
    // Nettoyer le timeout de notification
    if (this.notification.timeout) {
      clearTimeout(this.notification.timeout);
    }
  },
  methods: {
    async initializeData() {
      try {
        this.loading = true
        await this.loadChatbot()
        await this.loadVariables()
      } catch (error) {
        this.showNotification({ message: 'Erreur lors du chargement des données', type: 'error' })
        console.error('Erreur d\'initialisation:', error)
      } finally {
        this.loading = false
      }
    },

    async loadChatbot() {
      try {
        // D'abord essayer de récupérer depuis le sessionStorage
        const sessionChatbot = sessionStorage.getItem('Chatbot')
        if (sessionChatbot) {
          this.chatbot = JSON.parse(sessionChatbot)
          return
        }

        // Sinon faire un appel API
        const response = await axiosPeelo.get(`/get/chatbot/${this.chatbotId}`)
        if (response.data && response.data.length > 0) {
          this.chatbot = response.data[0]
          sessionStorage.setItem('Chatbot', JSON.stringify(this.chatbot))
        }
      } catch (error) {
        console.error('Erreur lors du chargement du chatbot:', error)
        throw error
      }
    },

    async loadVariables() {
      console.log('loadVariables')
      try {
        const response = await axiosPeelo.get(`/strong/get/variables/list/${this.chatbotId}`)
       
        console.log('response', response.data)

        if (response.data && response.data.success && response.data.chatbot && response.data.chatbot.length > 0) {
          const variablesData = response.data.chatbot[0]
          this.variables = variablesData.variables || []
        } else {
          this.variables = []
        }
      } catch (error) {
        console.error('Erreur lors du chargement des variables:', error)
        this.variables = []
      }
    },

    addVariable() {
      this.variables.push({
        id: '',
        slug: '',
        type: ''
      })
    },

    removeVariable(index) {
      this.variableToDelete = this.variables[index]
      this.indexToDelete = index
      this.showDeleteModal = true
    },

    confirmDelete() {
      if (this.indexToDelete !== null) {
        this.variables.splice(this.indexToDelete, 1)
        this.showNotification({ 
          message: 'Variable supprimée avec succès', 
          type: 'success' 
        })
      }
      this.cancelDelete()
    },

    cancelDelete() {
      this.showDeleteModal = false
      this.variableToDelete = null
      this.indexToDelete = null
    },

    validateVariable(variable) {
      // Auto-génération du slug basé sur l'ID si pas rempli
      if (variable.id && !variable.slug) {
        variable.slug = variable.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      }
    },

    getVariableErrors(variable) {
      const errors = []
      if (!variable.id) errors.push('L\'identifiant est requis')
      if (!variable.slug) errors.push('Le nom d\'affichage est requis')
      if (!variable.type) errors.push('Le type est requis')
      
      // Vérifier les doublons d'ID
      const duplicateIds = this.variables.filter(v => v.id === variable.id && v.id !== '').length
      if (duplicateIds > 1) {
        errors.push('Cet identifiant est déjà utilisé')
      }
      
      return errors
    },

    getTypeDescription(type) {
      return this.typeDescriptions[type] || ''
    },

    async saveVariables() {
      console.log('🚀 Début de saveVariables')
      console.log('📊 Variables à sauvegarder:', this.variables)
      console.log('🔑 ChatbotId:', this.chatbotId)
      console.log('✅ Form valid:', this.isFormValid) 
      
      if (!this.isFormValid) {
        console.log('❌ Formulaire invalide')
        this.showNotification({ message: 'Veuillez corriger les erreurs avant de sauvegarder', type: 'error' })
        return
      }

      try {
        this.saving = true
        console.log('💾 Début de sauvegarde...')
        
        // Afficher une notification de sauvegarde en cours
        this.showNotification({ message: 'Sauvegarde en cours...', type: 'saving', duration: 0 })
        
        // Format basé sur votre ancien code
        const variablesData = {
          variables: this.variables
        }
        
        console.log('📦 Données à envoyer (format ancien code):', variablesData)
        console.log('🌐 URL:', `/update/variable/declaration/${this.chatbotId}`)
        
        // Vérifier le token avant l'appel
        const userData = localStorage.getItem('user')
        console.log('👤 UserData présent:', !!userData)
        if (userData) {
          try {
            const userObj = JSON.parse(userData)
            console.log('🔐 Token présent:', !!userObj.data?.token)
            console.log('🔐 Token (début):', userObj.data?.token?.substring(0, 20) + '...')
          } catch (e) {
            console.log('❌ Erreur parsing userData:', e)
          }
        }

        // ESSAYONS D'ABORD AVEC L'URL SIMILAIRE AU GET
        console.log('📡 Test 1: Appel avec URL strong...')
         

                console.log('📡 Appel API avec URL corrigée...')
        console.log('📦 Données finales à envoyer:', variablesData.variables)

        const response = await axiosPeelo.post(`/strong/update/variable/declaration/${this.chatbotId}`, {
          variables: variablesData.variables
        })

        console.log('✅ Réponse reçue:', response)
        console.log('📄 Status:', response.status)
        console.log('📋 Data:', response.data)

        // Vérifier si on a une réponse de succès
        if (response.data && response.data.success) {
          console.log('✅ Sauvegarde réussie!')
          this.showNotification({ message: 'Variables sauvegardées avec succès', type: 'success' })
          
          // Optionnel: Recharger les variables depuis le serveur pour avoir la version à jour
          if (response.data.chatbot && response.data.chatbot.length > 0) {
            const updatedVariables = response.data.chatbot[0].variables
            if (updatedVariables) {
              this.variables = updatedVariables
              console.log('✅ Variables mises à jour depuis le serveur')
            }
          }
        } else if (response.data && response.data.message) {
          console.log('✅ Message de succès:', response.data.message)
          this.showNotification({ message: response.data.message, type: 'success' })
        } else {
          console.log('✅ Sauvegarde réussie (pas de message spécifique)')
          this.showNotification({ message: 'Variables sauvegardées avec succès', type: 'success' })
        }
      } catch (error) {
        console.log('❌ ERREUR DÉTECTÉE:')
        console.error('📛 Error object complet:', error)
        console.error('📛 Error message:', error.message)
        console.error('📛 Error response:', error.response)
        console.error('📛 Error response status:', error.response?.status)
        console.error('📛 Error response data:', error.response?.data)
        
        // Vérifier si c'est une erreur 401
        if (error.response?.status === 401) {
          console.log('🚨 ERREUR 401 - Non autorisé!')
          console.log('🚨 Ceci va déclencher la déconnexion automatique')
        }
        
        this.showNotification({ message: 'Erreur lors de la sauvegarde des variables', type: 'error' })
      } finally {
        console.log('🏁 Fin de saveVariables')
        this.saving = false
      }
    },

    goBack() {
      this.$router.go(-1)
    },

    showNotification({ message, type = 'success', duration = 3000 }) {
      if (this.notification.timeout) {
        clearTimeout(this.notification.timeout);
      }
      
      this.notification = {
        show: true,
        message,
        type,
        timeout: duration > 0 ? setTimeout(() => {
          this.notification.show = false;
        }, duration) : null
      };
    },

    // API Testing Methods
    testApiEndpoints() {
      this.showApiTest = true
      this.getTestResult = null
      this.postTestResult = null
    },

    closeApiTest() {
      this.showApiTest = false
    },

    async testGetVariables() {
      try {
        this.testingGet = true
        const response = await axiosPeelo.get(`/strong/get/variables/list/${this.chatbotId}`)
        this.getTestResult = JSON.stringify(response.data, null, 2)
      } catch (error) {
        this.getTestResult = JSON.stringify({
          error: error.message,
          response: error.response?.data
        }, null, 2)
      } finally {
        this.testingGet = false
      }
    },

    async testPostVariables() {
      try {
        this.testingPost = true
        
        const testData = [
          { id: 'test_var', slug: 'Variable Test', type: 'text' }
        ]
        
        console.log('🧪 Test POST avec données:', testData)
        
        const response = await axiosPeelo.post(`/strong/update/variable/declaration/${this.chatbotId}`, {
          variables: testData
        })
        
        this.postTestResult = JSON.stringify({
          success: true,
          url: '/strong/update/variable/declaration/',
          status: response.status,
          data: response.data
        }, null, 2)
        
      } catch (error) {
        this.postTestResult = JSON.stringify({
          error: error.message,
          status: error.response?.status,
          response: error.response?.data
        }, null, 2)
      } finally {
        this.testingPost = false
      }
    }
  }
}
</script>

<style scoped>


.main-content {
    flex: 1;
    margin-left: 40px !important;
 }


.variables-container {
  min-height: 100vh;
  background: transparent;
  padding: 20px;
}

/* Styles des notifications */
.notification-wrapper {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 10000;
  animation: slideIn 0.3s ease forwards;
}

@keyframes slideIn {
  from {
    transform: translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.notification {
  padding: 15px 25px;
  border-radius: 8px;
  animation: slideIn 0.3s ease-out;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 300px;
}

.notification.success {
  background-color: #4CAF50;
  color: white;
}

.notification.saved {
  background-color: #4CAF50;
  color: white;
}

.notification.saving {
  background-color: #ff9800;
  color: white;
}

.notification.error {
  background-color: #f44336;
  color: white;
}

.notification span {
  font-size: 14px;
  font-weight: 500;
}

.notification svg.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: #6b7280;
}

.loading-spinner svg {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Main Content */
.variables-content {
  max-width: 1200px;
  margin: 0 auto;
}

/* Header */
.variables-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
  background: rgba(255, 255, 255, 0.95);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.back-button {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  color: #374151;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  transition: all 0.2s;
}

.back-button:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
  transform: translateX(-2px);
}

.header-content {
  flex: 1;
}

.header-content h1 {
  color: #1f2937;
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 600;
}

.header-content p {
  color: #6b7280;
  margin: 0;
  font-size: 16px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.test-api-btn, .add-variable-btn {
  background: #10b981;
  border: none;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: all 0.2s;
}

.test-api-btn:hover, .add-variable-btn:hover {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

/* Variables List */
.variables-list {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
}

.empty-state svg {
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-state h3 {
  margin: 0 0 12px 0;
  color: #1e293b;
  font-size: 24px;
  font-weight: 600;
}

.empty-state p {
  margin: 0 0 30px 0;
  font-size: 16px;
}

/* Variables Grid */
.variables-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
  margin-bottom: 30px;
}

.variable-card {
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  background: #f8fafc;
  transition: all 0.2s;
}

.variable-card:hover {
  border-color: #10b981;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.1);
}

.variable-card.has-errors {
  border-color: #ef4444;
  background: #fef2f2;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.variable-index {
  background: #10b981;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
}

.delete-variable-btn {
  background: #fee2e2;
  border: none;
  color: #dc2626;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.delete-variable-btn:hover {
  background: #fecaca;
  transform: scale(1.1);
}

/* Variable Fields */
.variable-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-group label {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

.variable-input, .variable-select {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;
}

.variable-input:focus, .variable-select:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.variable-input.error, .variable-select.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.field-error {
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
}

.type-description {
  color: #6b7280;
  font-size: 13px;
  font-style: italic;
}

/* Validation Errors */
.validation-errors {
  margin-top: 16px;
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.error-item {
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 4px;
}

.error-item:last-child {
  margin-bottom: 0;
}

/* Actions */
.variables-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
  border-top: 2px solid #e5e7eb;
}

.primary-btn, .secondary-btn {
  padding: 14px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  border: none;
}

.primary-btn {
  background: #10b981;
  color: white;
}

.primary-btn:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.primary-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.secondary-btn {
  background: #f3f4f6;
  color: #374151;
  border: 2px solid #e5e7eb;
}

.secondary-btn:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-content.delete-modal {
  max-width: 450px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.delete-modal .modal-header {
  justify-content: flex-start;
  gap: 16px;
}

.modal-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.delete-icon {
  background: #fee2e2;
  color: #dc2626;
}

.modal-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 20px;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 24px;
}

.delete-modal .modal-body {
  text-align: center;
}

.delete-modal .modal-body p {
  margin: 0 0 16px 0;
  color: #6b7280;
  font-size: 16px;
}

.variable-preview {
  background: #f3f4f6;
  padding: 12px 16px;
  border-radius: 8px;
  margin: 16px 0;
  color: #374151;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 14px;
}

.warning-text {
  color: #dc2626 !important;
  font-weight: 500;
  font-size: 14px !important;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;
}

.delete-btn {
  background: #dc2626;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.delete-btn:hover {
  background: #b91c1c;
}

.cancel-btn {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.api-test-section {
  margin-bottom: 32px;
}

.api-test-section h4 {
  margin: 0 0 16px 0;
  color: #374151;
  font-size: 16px;
  font-weight: 600;
}

.test-btn {
  background: #10b981;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  margin-bottom: 16px;
  transition: all 0.2s;
}

.test-btn:hover:not(:disabled) {
  background: #059669;
}

.test-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.api-result {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 16px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Animations */
.spin {
  animation: spin 1s linear infinite;
}

/* Force margin-left pour main-content */
.variables-container {
  margin-left: 54px !important;
}

/* Override global main-content specifically for this view */
:deep(.main-content) {
  margin-left: 54px !important;
}

/* Responsive */
@media (max-width: 768px) {
  .variables-container {
    padding: 16px;
    margin-left: 54px !important;
  }
  
  :deep(.main-content) {
    margin-left: 54px !important;
  }
  
  .variables-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  
  .header-actions {
    justify-content: stretch;
  }
  
  .test-api-btn, .add-variable-btn {
    flex: 1;
    justify-content: center;
  }
  
  .variables-grid {
    grid-template-columns: 1fr;
  }
  
  .variables-actions {
    flex-direction: column;
    gap: 16px;
  }
  
  .primary-btn, .secondary-btn {
    width: 100%;
    justify-content: center;
  }
}
</style> 